import { useMemo, useState } from "preact/hooks";
import { Masthead } from "../components/Masthead";
import { idx, unitColor } from "../store";
import { toMin, fmt } from "../lib/text";
import { DAYS } from "../lib/planner";
import { day } from "../i18n";
import type { Day, Session } from "../lib/types";

function roomIndex() {
  const I = idx.value!;
  const m = new Map<string, { fac: string; room: string; sessions: Session[] }>();
  for (const s of I.core.sessions) {
    if (!s.room || (s.term && s.term !== "winter")) continue;
    const fac = I.courses.get(s.course_id)?.fac ?? "?";
    for (const r of s.room.split(/\s*[,/]\s*/).filter(Boolean)) {
      const k = `${fac}|${r}`;
      (m.get(k) ?? m.set(k, { fac, room: r, sessions: [] }).get(k)!).sessions.push(s);
    }
  }
  return m;
}

export function RoomsPage() {
  const I = idx.value!;
  const rooms = useMemo(roomIndex, [I]);
  const now = new Date();
  const [d, setD] = useState<Day>((DAYS[(now.getDay() + 6) % 7] ?? "Mon") as Day);
  const [from, setFrom] = useState(fmt(Math.max(8 * 60, Math.min(19 * 60, now.getHours() * 60 + (now.getMinutes() >= 30 ? 30 : 0)))));
  const [len, setLen] = useState(90);
  const [fac, setFac] = useState("");
  const a = toMin(from)!, b = a + len;
  const free = [...rooms.values()].filter((r) => (!fac || r.fac === fac) && !r.sessions.some((s) => s.day === d && toMin(s.start)! < b && a < toMin(s.end)!))
    .sort((x, y) => x.fac.localeCompare(y.fac) || x.room.localeCompare(y.room, "bs", { numeric: true }));
  document.title = "Slobodne sale · Slušam";
  return (
    <>
      <Masthead compact />
      <div class="page">
        <h1 style="font:400 44px/1 var(--display);margin:.2rem 0 .6rem">Slobodne sale</h1>
        <p class="note">Sale u kojima prema objavljenim rasporedima nema nastave u odabranom terminu. Ispiti i vanredni događaji nisu uključeni.</p>
        <div class="formrow">
          <label>Dan<select class="field" value={d} onChange={(e) => setD((e.target as HTMLSelectElement).value as Day)}>{DAYS.map((x) => <option key={x} value={x}>{day(x)}</option>)}</select></label>
          <label>Od<input class="field" type="time" value={from} onInput={(e) => setFrom((e.target as HTMLInputElement).value)} /></label>
          <label>Trajanje<select class="field" value={len} onChange={(e) => setLen(+(e.target as HTMLSelectElement).value)}>{[45, 60, 90, 120, 180].map((m) => <option key={m} value={m}>{m} min</option>)}</select></label>
          <label>Fakultet<select class="field" value={fac} onChange={(e) => setFac((e.target as HTMLSelectElement).value)}><option value="">svi</option>{I.core.units.map((u) => <option key={u.code}>{u.code}</option>)}</select></label>
        </div>
        <p class="note">{free.length} slobodnih sala · {day(d)} {from}–{fmt(b)}</p>
        <div class="roomgrid">{free.map((r) => <a key={r.fac + r.room} href={`/r/${r.fac}/${encodeURIComponent(r.room)}`} style={`--c:${unitColor(r.fac)}`}><b>{r.room}</b><span>{r.fac} · {r.sessions.length} termina sedmično</span></a>)}</div>
      </div>
    </>
  );
}

export function RoomPage({ fac, room }: { fac: string; room: string }) {
  const I = idx.value!;
  const r = useMemo(roomIndex, [I]).get(`${fac}|${decodeURIComponent(room)}`);
  const ss = (r?.sessions ?? []).sort((a, b) => DAYS.indexOf(a.day) - DAYS.indexOf(b.day) || toMin(a.start)! - toMin(b.start)!);
  document.title = `${decodeURIComponent(room)} · ${fac} · Slušam`;
  return (
    <>
      <Masthead compact />
      <div class="page">
        <div class="crumbs"><a href="/rooms">Sale</a> / <a href={`/u/${fac}`}>{fac}</a></div>
        <h1 style="font:400 44px/1 var(--display);margin:.2rem 0 .8rem">Sala {decodeURIComponent(room)}</h1>
        {DAYS.map((d) => { const ds = ss.filter((s) => s.day === d); return ds.length ? (
          <div key={d}><h3 style="font:500 20px var(--display);margin:1rem 0 .3rem">{day(d)}</h3>
            <table class="plain"><tbody>{ds.map((s) => { const c = I.courses.get(s.course_id)!; return <tr key={s.key}><td class="mono">{s.start}–{s.end}</td><td><a href={`/c/${encodeURIComponent(c.id)}`}>{c.name}</a></td><td>{s.kind}</td><td>{s.group}</td><td>{s.lecturer}</td></tr>; })}</tbody></table>
          </div>) : null; })}
        {!ss.length && <p class="note">Sala nije pronađena u rasporedima.</p>}
      </div>
    </>
  );
}
